import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { AmountInput, Icon, Label } from '@components/index';
import {
  GROUP_MEMBER_MAX_AMOUNT,
  GROUP_MEMBER_MIN_AMOUNT,
  SPLIT_REQUIRED_AMOUNT_MIN
} from '@constants/generic';
import { getFeeCategoryDataFromConfig } from '@features/group-management/helper';
import {
  getTermEndDate,
  initialTermsList
} from '@features/group-management/helpers/customTerm';
import {
  FeeCategoryDataType,
  FormDataType,
  MemberListType,
  TermsList,
  ValidationError
} from '@types';
import { formatISODate } from '@utils/date';
import {
  getAmountErrorMessage,
  getMaxAmountErrorMessage,
  sanitizeNumberInput
} from '@utils/generic';

import theme from '../../../../../../themes/colors';
import CustomTermTable from '../../v1/custom-term/CustomTermTable';
import FeeCategorySection from '../../v1/feeDetailsInput/FeeCategorySection';
import GroupDateSettings from '../../v1/feeDetailsInput/GroupDateSettings';
import InstallmentSection from '../../v1/feeDetailsInput/InstallmentSection';

interface FeeDetailsSectionProps {
  formData: FormDataType;
  onInputChange: (fieldName: string, value: any) => void;
  memberList?: MemberListType[] | [];
  setIsFeeCategoryEnabled: (value: boolean) => void;
  feeCategories: FeeCategoryDataType[];
  setFeeCategories: React.Dispatch<React.SetStateAction<FeeCategoryDataType[]>>;
  isFeeCategoryEnabled: boolean;
  branchDetails?: any;
  customTermDueDateError?: ValidationError;
  setCustomTermDueDateError?: (value: ValidationError) => void;
  error?: ValidationError;
  setError?: (value: ValidationError) => void;
  endDateError?: boolean;
  setEndDateError?: (value: boolean) => void;
  isEditGroup?: boolean;
}

const FeeDetailsSection = (props: FeeDetailsSectionProps) => {
  const {
    formData,
    onInputChange,
    memberList,
    setIsFeeCategoryEnabled,
    feeCategories,
    setFeeCategories,
    isFeeCategoryEnabled,
    branchDetails,
    customTermDueDateError,
    setCustomTermDueDateError,
    error,
    setError,
    endDateError,
    setEndDateError,
    isEditGroup = false
  } = props;

  const [feeCategory, setFeeCategory] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>(new Date().toString());

  const { orgConfig } = useSelector((state: any) => state.user);

  const groupMemberMaxAmount =
    orgConfig?.group_member_amount_max || GROUP_MEMBER_MAX_AMOUNT;
  const groupMemberMinAmount =
    orgConfig?.group_member_amount_min || GROUP_MEMBER_MIN_AMOUNT;
  const splitRequiredMinAmount =
    orgConfig?.split_required_amount_min || SPLIT_REQUIRED_AMOUNT_MIN;

  const defaultTermCategories = (
    feeCategoryData: {
      id: number;
      label: string;
      value: string;
    }[]
  ) =>
    feeCategoryData.map((item: any) => ({
      branch_fee_category_id: item.id,
      account_detail_id: item.id,
      category: item.label,
      amount: item.value
    }));

  const onAmountChange = (value: string) => {
    setError?.({ ...error, amount: '' });
    const sanitizedAmount = sanitizeNumberInput(value);
    if (Number(sanitizedAmount) > Number(groupMemberMaxAmount)) {
      setError?.({
        ...error,
        amount: `Amount cannot be greater than ${groupMemberMaxAmount}`
      });
    }
    onInputChange('amount', sanitizedAmount);
    if (
      Number(sanitizedAmount) < Number(splitRequiredMinAmount) &&
      formData?.paymentInstallment
    )
      onInputChange('paymentInstallment', false);
  };

  const onAmountBlur = (value: string) => {
    if (value.length && Number(value) < Number(groupMemberMinAmount))
      setError?.({
        ...error,
        amount: getAmountErrorMessage(groupMemberMinAmount)
      });

    if (value.length && Number(value) > Number(groupMemberMaxAmount))
      setError?.({
        ...error,
        amount: getMaxAmountErrorMessage(groupMemberMaxAmount)
      });
  };

  const updateTermsList = (termsList: TermsList[]) => {
    if (
      termsList.length > 0 &&
      formData.endDate &&
      new Date(
        new Date(termsList[termsList.length - 1].termEndDate).setHours(
          0,
          0,
          0,
          0
        )
      ) <= new Date(new Date(formData.endDate).setHours(0, 0, 0, 0))
    ) {
      setError?.({
        ...error,
        endDate: ''
      });
    }
    onInputChange('termsList', termsList);
  };

  useEffect(() => {
    if (formData.termsList && formData.isCustomTerm) {
      let totalAmount = 0;
      const defaultStartDate = startDate ? new Date(startDate) : new Date();
      let prevTermEndDate = new Date(
        defaultStartDate.setDate(defaultStartDate.getDate() - 1)
      ).toString();

      const updatedTermsList = formData.termsList.map((term) => {
        let { termStartDate, termEndDate, dueDate, feeCategoryData } = term;
        const { dueAmount, termDuration } = term;
        totalAmount += Number(dueAmount);

        if (feeCategories && (!feeCategoryData || feeCategoryData.length === 0))
          feeCategoryData = defaultTermCategories(feeCategories);

        if (isFeeCategoryEnabled && feeCategoryData) {
          const termFeeCategoryData =
            getFeeCategoryDataFromConfig(feeCategoryData);
          const totalFeeConfigAmount = termFeeCategoryData.reduce(
            (sum, category) => sum + (Number(category.value) || 0),
            0
          );

          if (Number(dueAmount) !== totalFeeConfigAmount)
            feeCategoryData = feeCategoryData.map((category, index) => ({
              ...category,
              amount: index === 0 ? dueAmount : '0'
            }));
        }

        const prevTermEndDateAsDate = new Date(prevTermEndDate);
        termStartDate =
          termStartDate ||
          new Date(
            prevTermEndDateAsDate.setDate(prevTermEndDateAsDate.getDate() + 1)
          ).toString();
        termEndDate =
          termEndDate || getTermEndDate(termStartDate, Number(termDuration));
        prevTermEndDate = termEndDate;

        if (
          dueDate &&
          (new Date(dueDate) > new Date(termEndDate) ||
            new Date(dueDate) < new Date(termStartDate))
        )
          dueDate = termStartDate;

        return {
          ...term,
          termStartDate,
          termEndDate,
          dueDate,
          feeCategoryData
        };
      });

      onInputChange('amount', totalAmount.toString());
      updateTermsList(updatedTermsList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(feeCategories),
    startDate,
    formData.isCustomTerm,
    isFeeCategoryEnabled
  ]);

  useEffect(() => {
    setFeeCategory(isFeeCategoryEnabled);
  }, [isFeeCategoryEnabled, feeCategories]);

  useEffect(() => {
    if (formData.isCustomTerm) {
      const formattedStartDate = formatISODate({
        date: new Date(),
        separator: '-',
        format: 'yyyy-mm-dd'
      });
      setStartDate(formattedStartDate);
      onInputChange('startDate', formattedStartDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.isCustomTerm]);

  return (
    <div className="rounded-xl border border-cofeeGreyBlue200 bg-cofeeNeutral1 py-4">
      <h3 className="border-b border-cofeeGreyBlue200 px-6 pb-4 text-lg font-semibold text-cofeeNeutral10">
        Fee details
      </h3>
      <div className="grid gap-y-4 p-6">
        {/* Frequency / Date Settings */}
        <GroupDateSettings
          formData={formData}
          groupId=""
          startDate={startDate}
          setStartDate={setStartDate}
          onInputChange={onInputChange}
          error={error}
          setError={setError}
          isEditGroup={isEditGroup}
          memberList={memberList}
          groupDateClassName="w-full md:w-1/2"
          groupStartDateClassName="w-full md:w-1/2"
          groupEndDateClassName="w-full md:w-1/2"
        />

        {/* Amount */}
        <div className="w-full md:w-1/2 lg:w-1/3">
          <Label label="Amount" className="!mb-2 !text-content" isRequired />
          <AmountInput
            value={formData.amount}
            onAmountChange={onAmountChange}
            handleInputBlur={onAmountBlur}
            error={!formData.isCustomTerm && !!error?.amount}
            errorMessage={!formData.isCustomTerm ? error?.amount : ''}
            disabled={feeCategory || formData.isCustomTerm}
            className="!bg-white"
            inputClassNames={`${
              feeCategory || formData.isCustomTerm
                ? 'cursor-not-allowed opacity-40'
                : ''
            }`}
            maxAmount={groupMemberMaxAmount}
          />
          {!formData.isCustomTerm && (
            <div className="mt-2 flex items-center gap-1 text-sm text-grey60">
              <Icon name="alert-circle" stroke={theme.subText} size="16" />
              {`You can enter any amount between ${groupMemberMinAmount} and ${groupMemberMaxAmount}`}
            </div>
          )}
        </div>

        <div className="my-2 h-px w-full bg-cofeeGreyBlue200" />

        {/* Fee Category */}
        <FeeCategorySection
          isCustomTerm={!!formData.isCustomTerm}
          isEditGroup={isEditGroup}
          setIsFeeCategoryEnabled={setIsFeeCategoryEnabled}
          setFeeCategory={setFeeCategory}
          groupId=""
          feeCategories={feeCategories}
          setFeeCategories={setFeeCategories}
          onInputChange={onInputChange}
          isFeeConfigured={
            isEditGroup
              ? formData?.feeCategoryEnabled
              : branchDetails?.is_fee_configured
          }
          isFeeCategoryEnabled={isFeeCategoryEnabled}
          feeCategory={feeCategory}
        />

        {/* Custom Term Table */}
        {formData.isCustomTerm && (
          <CustomTermTable
            termsList={formData.termsList || [initialTermsList]}
            setTermsList={updateTermsList}
            isFeeCategoryEnabled={isFeeCategoryEnabled}
            feeCategories={feeCategories}
            onAmountChange={(amount) => onInputChange('amount', amount)}
            isEditGroup={isEditGroup}
            setError={setCustomTermDueDateError}
            error={customTermDueDateError}
            groupEndDate={formData.endDate}
            endDateError={endDateError}
            setEndDateError={setEndDateError}
          />
        )}

        {/* Installment */}
        <InstallmentSection
          formData={formData}
          onInputChange={onInputChange}
          isEnabled={false} // Update this true if installment allow for always
        />
      </div>
    </div>
  );
};

export default FeeDetailsSection;
